import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMagazine, getArticles, downloadPDF, trackView } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import CenturionFooter from '@/components/CenturionFooter';
import {
  ArrowLeft,
  Share2,
  Download,
  Mail,
  MessageCircle,
  Copy,
  Check,
  BookOpen,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

// Template-specific styles
const templateStyles = {
  classic: {
    bg: 'bg-[#FDFBF7]',
    accent: '#8B4513',
    accentBg: 'bg-[#8B4513]',
    coverGradient: 'from-[#8B4513]/80 to-[#D2691E]/60',
    sectionBg: 'bg-gradient-to-r from-[#FFF8DC] to-[#FAEBD7]',
    articleBg: 'bg-white',
    titleFont: 'font-serif',
    bodyFont: 'font-serif',
    borderColor: 'border-[#D2B48C]',
  },
  modern: {
    bg: 'bg-slate-50',
    accent: '#0066CC',
    accentBg: 'bg-[#0066CC]',
    coverGradient: 'from-slate-900/90 to-slate-700/80',
    sectionBg: 'bg-gradient-to-r from-slate-100 to-slate-50',
    articleBg: 'bg-white',
    titleFont: 'font-sans',
    bodyFont: 'font-sans',
    borderColor: 'border-slate-200',
  },
  tropical: {
    bg: 'bg-gradient-to-b from-[#E0F7FA] to-[#FFF8E1]',
    accent: '#00897B',
    accentBg: 'bg-gradient-to-r from-[#00897B] to-[#26A69A]',
    coverGradient: 'from-[#00897B]/80 to-[#4DD0E1]/60',
    sectionBg: 'bg-gradient-to-r from-[#E0F7FA] to-[#B2EBF2]',
    articleBg: 'bg-white/90 backdrop-blur-sm',
    titleFont: 'font-serif',
    bodyFont: 'font-sans',
    borderColor: 'border-[#80CBC4]',
  },
  minimalist: {
    bg: 'bg-white',
    accent: '#1a1a1a',
    accentBg: 'bg-[#1a1a1a]',
    coverGradient: 'from-black/70 to-black/50',
    sectionBg: 'bg-gray-50',
    articleBg: 'bg-white',
    titleFont: 'font-sans',
    bodyFont: 'font-sans',
    borderColor: 'border-gray-100',
  },
  bold: {
    bg: 'bg-gradient-to-br from-[#FF6B6B] via-[#4ECDC4] to-[#45B7D1]',
    accent: '#FF6B6B',
    accentBg: 'bg-gradient-to-r from-[#FF6B6B] to-[#FFE66D]',
    coverGradient: 'from-[#FF6B6B]/90 to-[#4ECDC4]/80',
    sectionBg: 'bg-white/95 backdrop-blur-md',
    articleBg: 'bg-white/95 backdrop-blur-md',
    titleFont: 'font-sans font-black',
    bodyFont: 'font-sans',
    borderColor: 'border-white/30',
  },
};

const MagazineView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [magazine, setMagazine] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [magRes, artRes] = await Promise.all([
          getMagazine(id),
          getArticles(id),
        ]);
        setMagazine(magRes.data);
        setArticles(artRes.data);
        
        // Track the view
        try {
          await trackView(id);
        } catch (e) {
          // Silent fail for tracking
        }
      } catch (error) {
        toast.error('Failed to load magazine');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const template = magazine?.template || 'classic';
  const styles = templateStyles[template] || templateStyles.classic;

  const getShareUrl = () => window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`Check out: ${magazine?.title}`);
    const body = encodeURIComponent(`I'd like to share this magazine with you:\n\n${magazine?.title}\n${magazine?.description || ''}\n\nView it here: ${getShareUrl()}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(`Check out this magazine: ${magazine?.title}\n${getShareUrl()}`);
    window.open(`https://wa.me/?text=${text}`);
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await downloadPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${magazine?.title || 'magazine'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const getArticlesBySection = () => {
    const grouped = {};
    magazine?.sections?.forEach(section => {
      grouped[section.id] = {
        section,
        articles: articles.filter(a => a.section_id === section.id).sort((a, b) => a.order - b.order),
      };
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="spinner" style={{ width: '48px', height: '48px' }} />
      </div>
    );
  }

  const groupedArticles = getArticlesBySection();

  // Render based on template
  const renderCover = () => {
    switch (template) {
      case 'modern':
        return (
          <div className="relative h-[70vh] min-h-[500px] overflow-hidden">
            {magazine?.cover_image ? (
              <img src={magazine.cover_image} alt={magazine.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-600" />
            )}
            <div className={`absolute inset-0 bg-gradient-to-t ${styles.coverGradient}`} />
            <div className="absolute inset-0 flex flex-col justify-end p-12">
              <div className="max-w-4xl">
                <h1 className={`${styles.titleFont} text-5xl md:text-7xl text-white font-bold tracking-tight mb-4`}>
                  {magazine?.title}
                </h1>
                {magazine?.description && (
                  <p className="text-white/90 text-xl max-w-2xl">{magazine.description}</p>
                )}
              </div>
            </div>
          </div>
        );
      
      case 'tropical':
        return (
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#00897B]/20 to-[#4DD0E1]/20" />
            {magazine?.cover_image ? (
              <div className="relative">
                <img src={magazine.cover_image} alt={magazine.title} className="w-full h-[60vh] object-cover" />
                <div className={`absolute inset-0 bg-gradient-to-t ${styles.coverGradient}`} />
                <div className="absolute bottom-0 left-0 right-0 p-10">
                  <h1 className={`${styles.titleFont} text-5xl md:text-6xl text-white tracking-tight mb-3 drop-shadow-lg`}>
                    {magazine?.title}
                  </h1>
                  {magazine?.description && (
                    <p className="text-white/90 text-lg max-w-2xl drop-shadow">{magazine.description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-16 text-center bg-gradient-to-br from-[#E0F7FA] to-[#B2EBF2]">
                <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-r from-[#00897B] to-[#26A69A] flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-white" strokeWidth={1.5} />
                </div>
                <h1 className={`${styles.titleFont} text-5xl text-[#00695C] tracking-tight mb-4`}>
                  {magazine?.title}
                </h1>
                {magazine?.description && (
                  <p className="text-[#00897B] text-lg max-w-2xl mx-auto">{magazine.description}</p>
                )}
              </div>
            )}
          </div>
        );
      
      case 'minimalist':
        return (
          <div className="max-w-3xl mx-auto py-20 px-8 text-center">
            <h1 className={`${styles.titleFont} text-6xl md:text-8xl text-[#1a1a1a] tracking-tighter mb-6 font-light`}>
              {magazine?.title}
            </h1>
            <div className="w-16 h-[1px] bg-[#1a1a1a] mx-auto mb-6" />
            {magazine?.description && (
              <p className="text-gray-500 text-lg max-w-xl mx-auto">{magazine.description}</p>
            )}
            {magazine?.cover_image && (
              <div className="mt-12">
                <img src={magazine.cover_image} alt={magazine.title} className="w-full max-h-[50vh] object-cover" />
              </div>
            )}
          </div>
        );
      
      case 'bold':
        return (
          <div className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
            {magazine?.cover_image ? (
              <>
                <img src={magazine.cover_image} alt={magazine.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B6B]/80 via-[#4ECDC4]/60 to-[#45B7D1]/80" />
              </>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B6B] via-[#4ECDC4] to-[#45B7D1]" />
            )}
            <div className="relative z-10 text-center p-8">
              <h1 className={`${styles.titleFont} text-6xl md:text-8xl text-white tracking-tight mb-6 uppercase`}>
                {magazine?.title}
              </h1>
              {magazine?.description && (
                <p className="text-white/90 text-xl max-w-2xl mx-auto font-medium">{magazine.description}</p>
              )}
            </div>
          </div>
        );
      
      default: // classic
        return (
          <div className="magazine-page mb-8 overflow-hidden">
            {magazine?.cover_image ? (
              <div className="aspect-[16/9] relative">
                <img src={magazine.cover_image} alt={magazine.title} className="w-full h-full object-cover" />
                <div className={`absolute inset-0 bg-gradient-to-t ${styles.coverGradient}`} />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h1 className={`${styles.titleFont} text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-2`}>
                    {magazine?.title}
                  </h1>
                  {magazine?.description && (
                    <p className="text-white/80 text-lg max-w-2xl">{magazine.description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-12 text-center bg-gradient-to-br from-[#FFF8DC] to-[#FAEBD7]">
                <BookOpen className="w-20 h-20 text-[#8B4513] mx-auto mb-6" strokeWidth={1} />
                <h1 className={`${styles.titleFont} text-4xl sm:text-5xl text-[#5D3A1A] tracking-tight mb-4`}>
                  {magazine?.title}
                </h1>
                {magazine?.description && (
                  <p className="text-[#8B7355] text-lg max-w-2xl mx-auto">{magazine.description}</p>
                )}
              </div>
            )}
          </div>
        );
    }
  };

  const renderSectionHeader = (section, index) => {
    switch (template) {
      case 'modern':
        return (
          <div className={`${styles.sectionBg} p-8 mb-6 border-l-4`} style={{ borderLeftColor: styles.accent }}>
            <span className="text-sm font-bold tracking-widest uppercase" style={{ color: styles.accent }}>
              Section {index + 1}
            </span>
            <h2 className={`${styles.titleFont} text-4xl text-slate-800 mt-2 font-bold`}>{section.name}</h2>
          </div>
        );
      
      case 'tropical':
        return (
          <div className={`${styles.sectionBg} p-8 mb-6 rounded-2xl border ${styles.borderColor}`}>
            <span className="text-xs tracking-[0.3em] uppercase font-semibold text-[#00897B] block mb-2">
              Section {index + 1}
            </span>
            <h2 className={`${styles.titleFont} text-3xl text-[#00695C]`}>{section.name}</h2>
          </div>
        );
      
      case 'minimalist':
        return (
          <div className="py-12 mb-8 border-t border-b border-gray-200">
            <span className="text-xs tracking-[0.3em] uppercase text-gray-400 block mb-3">
              {String(index + 1).padStart(2, '0')}
            </span>
            <h2 className={`${styles.titleFont} text-3xl text-[#1a1a1a] font-light tracking-tight`}>{section.name}</h2>
          </div>
        );
      
      case 'bold':
        return (
          <div className={`${styles.sectionBg} p-8 mb-6 rounded-3xl`}>
            <span className="inline-block px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#FF6B6B] to-[#FFE66D] mb-3">
              Section {index + 1}
            </span>
            <h2 className={`${styles.titleFont} text-4xl text-gray-800 uppercase`}>{section.name}</h2>
          </div>
        );
      
      default:
        return (
          <div className={`${styles.sectionBg} p-8 mb-6 rounded-lg border ${styles.borderColor}`}>
            <span className="text-xs tracking-[0.2em] uppercase font-medium text-[#8B4513] block mb-2">
              Section {index + 1}
            </span>
            <h2 className={`${styles.titleFont} text-3xl text-[#5D3A1A]`}>{section.name}</h2>
          </div>
        );
    }
  };

  const renderArticleContent = (content) => {
    // Check if content contains HTML tags
    if (content.includes('<')) {
      return { __html: content };
    }
    // Plain text fallback - wrap paragraphs
    return { __html: content.split('\n').filter(p => p.trim()).map(p => `<p>${p}</p>`).join('') };
  };

  const renderArticle = (article, template) => {
    switch (template) {
      case 'modern':
        return (
          <article className={`${styles.articleBg} mb-8 rounded-xl overflow-hidden shadow-lg border ${styles.borderColor}`}>
            {article.image_url && (
              <img src={article.image_url} alt={article.title} className="w-full h-64 object-cover" />
            )}
            <div className="p-8">
              <h3 className={`${styles.titleFont} text-2xl sm:text-3xl text-slate-800 mb-4 font-bold`}>
                {article.title}
              </h3>
              <div
                className={`${styles.bodyFont} text-slate-600 leading-relaxed text-lg article-content`}
                dangerouslySetInnerHTML={renderArticleContent(article.content)}
              />
            </div>
          </article>
        );
      
      case 'tropical':
        return (
          <article className={`${styles.articleBg} mb-8 rounded-2xl overflow-hidden shadow-md border ${styles.borderColor}`}>
            {article.image_url && (
              <div className="relative">
                <img src={article.image_url} alt={article.title} className="w-full h-72 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#00897B]/20 to-transparent" />
              </div>
            )}
            <div className="p-8">
              <h3 className={`${styles.titleFont} text-2xl sm:text-3xl text-[#00695C] mb-4`}>
                {article.title}
              </h3>
              <div
                className={`${styles.bodyFont} text-[#37474F] leading-relaxed article-content`}
                dangerouslySetInnerHTML={renderArticleContent(article.content)}
              />
            </div>
          </article>
        );
      
      case 'minimalist':
        return (
          <article className="mb-16">
            {article.image_url && (
              <img src={article.image_url} alt={article.title} className="w-full mb-8" />
            )}
            <h3 className={`${styles.titleFont} text-3xl text-[#1a1a1a] mb-6 font-light tracking-tight`}>
              {article.title}
            </h3>
            <div
              className={`${styles.bodyFont} text-gray-600 leading-loose text-lg article-content`}
              dangerouslySetInnerHTML={renderArticleContent(article.content)}
            />
          </article>
        );
      
      case 'bold':
        return (
          <article className={`${styles.articleBg} mb-8 rounded-3xl overflow-hidden shadow-xl`}>
            {article.image_url && (
              <img src={article.image_url} alt={article.title} className="w-full h-80 object-cover" />
            )}
            <div className="p-10">
              <h3 className={`${styles.titleFont} text-3xl text-gray-800 mb-4 uppercase tracking-wide`}>
                {article.title}
              </h3>
              <div
                className={`${styles.bodyFont} text-gray-600 leading-relaxed text-lg article-content`}
                dangerouslySetInnerHTML={renderArticleContent(article.content)}
              />
            </div>
          </article>
        );
      
      default:
        return (
          <article className={`${styles.articleBg} p-8 mb-6 rounded-lg shadow-sm border ${styles.borderColor}`}>
            {article.image_url && (
              <div className="aspect-[16/9] -mx-8 -mt-8 mb-6 overflow-hidden rounded-t-lg">
                <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
              </div>
            )}
            <h3 className={`${styles.titleFont} text-2xl sm:text-3xl text-[#5D3A1A] mb-4`}>
              {article.title}
            </h3>
            <div
              className={`${styles.bodyFont} text-[#6B5344] leading-relaxed article-content`}
              dangerouslySetInnerHTML={renderArticleContent(article.content)}
            />
          </article>
        );
    }
  };

  return (
    <div className={`min-h-screen ${styles.bg}`}>
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-[#4A5568] hover:text-[#0A192F]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#4A5568] hidden sm:inline capitalize px-2 py-1 bg-white/50 rounded-full">
              {template} template
            </span>
            <Button
              data-testid="view-share-btn"
              variant="outline"
              size="sm"
              onClick={() => setIsShareOpen(true)}
              className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button
              data-testid="view-download-btn"
              size="sm"
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              className="btn-coral"
            >
              {downloadingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" />PDF</>}
            </Button>
          </div>
        </div>
      </header>

      {/* Cover */}
      {renderCover()}

      {/* Content */}
      <main className={`py-12 px-4 ${template === 'minimalist' ? 'max-w-3xl' : 'max-w-4xl'} mx-auto`}>
        {/* Table of Contents (only for classic and tropical) */}
        {['classic', 'tropical'].includes(template) && magazine?.sections?.length > 0 && (
          <div className={`${styles.articleBg} p-8 mb-12 rounded-lg border ${styles.borderColor}`}>
            <h2 className={`${styles.titleFont} text-2xl mb-6 pb-3 border-b ${styles.borderColor}`}
                style={{ color: template === 'tropical' ? '#00695C' : '#5D3A1A' }}>
              Contents
            </h2>
            <div className="space-y-4">
              {magazine.sections.map((section, index) => {
                const sectionArticles = articles.filter(a => a.section_id === section.id);
                if (sectionArticles.length === 0) return null;
                return (
                  <div key={section.id} className="flex items-baseline gap-4">
                    <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{ color: styles.accent }}>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1">
                      <h3 className={`${styles.titleFont} text-lg`} style={{ color: template === 'tropical' ? '#00695C' : '#5D3A1A' }}>
                        {section.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {sectionArticles.length} article{sectionArticles.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Articles by Section */}
        {Object.values(groupedArticles).map(({ section, articles: sectionArticles }, sectionIndex) => {
          if (sectionArticles.length === 0) return null;
          
          return (
            <div key={section.id} className="mb-16">
              {renderSectionHeader(section, sectionIndex)}
              
              {/* Grid layout for modern and bold templates */}
              {['modern', 'bold'].includes(template) ? (
                <div className={sectionArticles.length > 1 ? 'grid md:grid-cols-2 gap-6' : ''}>
                  {sectionArticles.map((article) => (
                    <div key={article.id} data-testid={`view-article-${article.id}`}>
                      {renderArticle(article, template)}
                    </div>
                  ))}
                </div>
              ) : (
                sectionArticles.map((article) => (
                  <div key={article.id} data-testid={`view-article-${article.id}`}>
                    {renderArticle(article, template)}
                  </div>
                ))
              )}
            </div>
          );
        })}

        {/* Footer */}
        <div className={`${styles.articleBg} p-8 text-center rounded-lg border ${styles.borderColor}`}>
          <img 
            src="https://customer-assets.emergentagent.com/job_magzine-maker/artifacts/c7i8lwjh_image.png" 
            alt="Treasure Cove Logo" 
            className="h-16 object-contain mx-auto mb-4"
          />
          <p className="text-sm text-gray-500">
            Created with Treasure Cove Community Magazine Creator
          </p>
        </div>
      </main>

      {/* Share Dialog */}
      <Dialog open={isShareOpen} onOpenChange={setIsShareOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#FF6F61]" />
              Share Magazine
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Share Link</label>
              <div className="flex gap-2">
                <Input readOnly value={getShareUrl()} className="input-bahamian flex-1" />
                <Button data-testid="view-copy-link-btn" onClick={handleCopyLink} variant="outline" className="border-[#E8E2D2]">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button data-testid="view-share-email-btn" onClick={handleShareEmail} variant="outline" className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]">
                <Mail className="w-4 h-4 mr-2" />Email
              </Button>
              <Button data-testid="view-share-whatsapp-btn" onClick={handleShareWhatsApp} variant="outline" className="border-[#E8E2D2] text-green-600 hover:bg-green-50">
                <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
              </Button>
            </div>
            <Button data-testid="view-download-pdf-btn" onClick={handleDownloadPDF} disabled={downloadingPDF} className="btn-coral w-full">
              {downloadingPDF ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating PDF...</> : <><Download className="w-4 h-4 mr-2" />Download PDF</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <CenturionFooter />
    </div>
  );
};

export default MagazineView;
